import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

interface Webhook {
  id: string;
  name: string;
  url: string;
  events: string[];
  active: boolean;
  secret: string;
  created_at: string;
  last_triggered?: string;
  success_rate?: number;
}

interface NewWebhook {
  name: string;
  url: string;
  events: string[];
  secret?: string;
}

export function useWebhooks() {
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchWebhooks = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('webhooks')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setWebhooks(data || []);
      setError(null);
    } catch (err) {
      console.error('Erro ao buscar webhooks:', err);
      setError('Não foi possível carregar os webhooks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWebhooks();

    // Inscreve para atualizações em tempo real
    const channel = supabase
      .channel('webhooks_changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'webhooks'
      }, () => {
        fetchWebhooks();
      })
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [fetchWebhooks]);

  const createWebhook = async (webhook: NewWebhook) => {
    try {
      const { data, error } = await supabase
        .from('webhooks')
        .insert([{
          ...webhook,
          active: true,
          secret: webhook.secret || crypto.randomUUID(),
        }])
        .select()
        .single();

      if (error) throw error;

      setWebhooks(prev => [data, ...prev]);
      return data;
    } catch (err) {
      console.error('Erro ao criar webhook:', err);
      throw new Error('Não foi possível criar o webhook');
    }
  };

  const deleteWebhook = async (id: string) => {
    try {
      const { error } = await supabase
        .from('webhooks')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setWebhooks(prev => prev.filter(webhook => webhook.id !== id));
    } catch (err) {
      console.error('Erro ao deletar webhook:', err);
      throw new Error('Não foi possível deletar o webhook');
    }
  };

  const updateWebhook = async (id: string, updates: Partial<Webhook>) => {
    try {
      const { data, error } = await supabase
        .from('webhooks')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setWebhooks(prev => prev.map(webhook => 
        webhook.id === id ? { ...webhook, ...data } : webhook
      ));

      return data;
    } catch (err) {
      console.error('Erro ao atualizar webhook:', err);
      throw new Error('Não foi possível atualizar o webhook');
    }
  };

  const testWebhook = async (id: string) => {
    try {
      const webhook = webhooks.find(w => w.id === id);
      if (!webhook) throw new Error('Webhook não encontrado');

      // Envia um evento de teste
      const testEvent = {
        type: 'test',
        webhook_id: id,
        timestamp: new Date().toISOString(),
        data: {
          message: 'Este é um evento de teste',
          webhook_name: webhook.name
        }
      };

      const response = await fetch(webhook.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Webhook-Secret': webhook.secret
        },
        body: JSON.stringify(testEvent)
      });

      if (!response.ok) {
        throw new Error(`Erro ao testar webhook: ${response.statusText}`);
      }

      // Atualiza o último disparo e taxa de sucesso
      await updateWebhook(id, {
        last_triggered: new Date().toISOString(),
        success_rate: 100
      });

      alert('Teste de webhook realizado com sucesso!');
    } catch (err) {
      console.error('Erro ao testar webhook:', err);
      alert('Erro ao testar webhook. Verifique o console para mais detalhes.');
    }
  };

  return {
    webhooks,
    loading,
    error,
    createWebhook,
    deleteWebhook,
    updateWebhook,
    testWebhook,
    refresh: fetchWebhooks
  };
}